import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { SHEET_MAX, sheetMaxHeight } from '../lib/composerModel';

interface Metrics {
  /** Предельная высота листа над строкой. */
  maxSheet: number;
  /** Высота видимой части main — затемнение листа закрывает её целиком (ТЗ § 4). */
  mainHeight: number;
  /** Строка сверху: от верха main до верха полосы — затемнение сдвигается на столько же и закрывает весь main. */
  dockOffset: number;
}

/**
 * Замеры открытого листа (ТЗ главной § 6): верх — не выше низа блока тренировки + 24 px, пока он
 * виден, не выше верха main (или видимой области при клавиатуре iPad) + 16 px и 600 px; затемнение —
 * на всю высоту main. Меряем при открытии и при изменении окна и `visualViewport`. Пока лист открыт,
 * прокрутка главной заблокирована: `scrollbar-gutter: stable` у main, поэтому ничего не сдвигается.
 */
export function useSheetMetrics(
  open: boolean,
  {
    dock,
    row,
    hero,
    below = false,
  }: { dock: RefObject<HTMLElement>; row: RefObject<HTMLElement>; hero?: RefObject<HTMLElement>; below?: boolean },
): Metrics {
  const [metrics, setMetrics] = useState<Metrics>({ maxSheet: SHEET_MAX, mainHeight: 0, dockOffset: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const main = dock.current?.closest('main');
      const rowEl = row.current;
      if (!main || !rowEl) return;
      const mainRect = main.getBoundingClientRect();
      const heroRect = hero?.current?.getBoundingClientRect();
      const viewportTop = window.visualViewport?.offsetTop ?? 0;
      const rowRect = rowEl.getBoundingClientRect();
      const dockRect = dock.current?.getBoundingClientRect();
      const next: Metrics = {
        // Строка сверху — лист раскрывается вниз между полем и рядом чипов: до низа main без строки и поля.
        maxSheet: below
          ? Math.max(0, Math.min(SHEET_MAX, mainRect.bottom - rowRect.top - rowRect.height - 24))
          : sheetMaxHeight({
              rowTop: rowRect.top,
              areaTop: Math.max(mainRect.top, viewportTop),
              heroBottom: heroRect && heroRect.height > 0 ? heroRect.bottom : null,
            }),
        mainHeight: main.clientHeight,
        // Может быть и отрицательным: поднятая на верхнюю полосу строка выше верха main.
        dockOffset: below && dockRect ? dockRect.top - mainRect.top : 0,
      };
      setMetrics((prev) =>
        prev.maxSheet === next.maxSheet && prev.mainHeight === next.mainHeight && prev.dockOffset === next.dockOffset
          ? prev
          : next,
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [open, dock, row, hero, below]);

  // Прокрутка главной под открытым листом заблокирована; колесо над листом крутит сам лист.
  useEffect(() => {
    if (!open) return;
    const main = dock.current?.closest('main');
    if (!main) return;
    const previous = main.style.overflowY;
    main.style.overflowY = 'hidden';
    return () => {
      main.style.overflowY = previous;
    };
  }, [open, dock]);

  return metrics;
}
